// File: src/components/SeriesClassManagement.js
// The classes a series runs, and the scoring rules each of them may bend.
//
// Every class follows its series unless it says otherwise, and "follows the
// series" is a real stored answer rather than an empty field: a class that
// overrides races counted to 0 means "count every race", which is not the same
// as having no opinion. That distinction is the whole reason the columns are
// nullable, so the form has to keep it - a checkbox for "follow the series",
// and the control only in play once it is cleared.

import React, { useState } from 'react';
import { Plus, Trash2, Edit, Check, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiPost, apiPut, apiDelete } from '../services/api';
import { SCORING_FIELDS } from '../utils/scoring';

const describe = (err, fallback) => (err.body && err.body.message) || err.message || fallback;

function SeriesClassManagement({ series, token, onChange }) {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const classes = series.classes || [];

  const addClass = async () => {
    if (!name.trim()) {
      setError(t('seriesClassNameRequired'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      await apiPost(`/series/${series.id}/classes`, {
        name: name.trim(), sort_order: classes.length
      }, { token });
      setName('');
      onChange();
    } catch (err) {
      setError(describe(err, t('saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const saveName = async (cls) => {
    if (!editName.trim()) {
      setError(t('seriesClassNameRequired'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      await apiPut(`/series-classes/${cls.id}`, { name: editName.trim() }, { token });
      setEditingId(null);
      onChange();
    } catch (err) {
      setError(describe(err, t('saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const removeClass = async (cls) => {
    if (!window.confirm(t('deleteSeriesClassConfirm'))) return;

    setError('');
    try {
      await apiDelete(`/series-classes/${cls.id}`, { token });
      onChange();
    } catch (err) {
      // The server refuses a class with results, and the last class of a
      // series, and explains which - surface that rather than a generic failure.
      if (err.body && err.body.error === 'lastClass') {
        setError(t('seriesClassLastCannotDelete'));
        return;
      }
      setError(describe(err, t('failedToDeleteClass')));
    }
  };

  const saveSetting = async (cls, update) => {
    setError('');
    try {
      await apiPut(`/series-classes/${cls.id}`, update, { token });
      onChange();
    } catch (err) {
      setError(describe(err, t('saveFailed')));
    }
  };

  // One row per rule. `overrides[column] === null` is the class saying nothing,
  // which is why the checkbox is keyed on that and not on the effective value.
  const settingRow = (cls, field) => {
    const inherits = cls.overrides[field.column] === null
      || cls.overrides[field.column] === undefined;
    const effective = cls[field.column];

    return (
      <div key={field.column} className="flex items-center gap-3 py-1">
        <label className="flex items-center gap-2 text-sm text-ink-muted w-52 shrink-0">
          <input
            type="checkbox"
            checked={inherits}
            // Unchecking sends the value the class is already racing under, so
            // nothing changes on screen at the moment it stops inheriting.
            onChange={(e) => saveSetting(cls, {
              [field.api]: e.target.checked ? null : effective
            })}
            className="w-4 h-4"
          />
          {t('inheritFromSeries')}
        </label>

        <span className="text-ink text-sm flex-1">{t(field.label)}</span>

        {field.type === 'bool' ? (
          <input
            type="checkbox"
            checked={Boolean(effective)}
            disabled={inherits}
            onChange={(e) => saveSetting(cls, { [field.api]: e.target.checked })}
            className="w-5 h-5 disabled:opacity-40"
          />
        ) : (
          <input
            type="number"
            min="0"
            value={effective ?? 0}
            disabled={inherits}
            onChange={(e) => saveSetting(cls, { [field.api]: parseInt(e.target.value, 10) || 0 })}
            className="w-24 px-2 py-1 bg-paper-alt border border-line rounded text-ink text-sm disabled:opacity-40"
          />
        )}
      </div>
    );
  };

  return (
    <div className="mt-6 border-t border-line pt-6">
      <h3 className="text-lg font-bold text-ink mb-1">{t('seriesClasses')}</h3>
      <p className="text-ink-muted text-sm mb-4">{t('seriesClassHelp')}</p>

      <div className="space-y-4 mb-4">
        {classes.map(cls => (
          <div key={cls.id} className="bg-paper-alt rounded p-4">
            <div className="flex items-center gap-3 mb-3">
              {editingId === cls.id ? (
                <>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="flex-1 px-3 py-1 bg-paper border border-line rounded text-ink"
                  />
                  <button onClick={() => saveName(cls)} disabled={saving}
                    className="text-green-700 dark:text-green-400 hover:opacity-80" title={t('save')}>
                    <Check className="w-5 h-5" />
                  </button>
                  <button onClick={() => setEditingId(null)}
                    className="text-ink-muted hover:text-ink" title={t('cancel')}>
                    <X className="w-5 h-5" />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-ink font-semibold">{cls.name}</span>
                  <span className="text-ink-muted text-sm">
                    {(cls.races || []).length} {t('races')} · {(cls.drivers || []).length} {t('drivers')}
                  </span>
                  <button
                    onClick={() => { setEditingId(cls.id); setEditName(cls.name); setError(''); }}
                    className="text-blue-700 dark:text-blue-400 hover:opacity-80" title={t('edit')}>
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => removeClass(cls)}
                    className="text-red-700 dark:text-red-400 hover:opacity-80" title={t('delete')}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>

            <details>
              <summary className="text-ink-muted text-sm cursor-pointer">{t('classSettings')}</summary>
              <div className="mt-2">
                {SCORING_FIELDS.map(field => settingRow(cls, field))}
              </div>
            </details>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('seriesClassName')}
          className="flex-1 px-4 py-2 bg-paper-alt border border-line rounded text-ink"
        />
        <button
          onClick={addClass}
          disabled={saving}
          className="bg-paper-alt hover:opacity-80 disabled:opacity-50 text-ink px-4 py-2 rounded font-semibold flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('addSeriesClass')}
        </button>
      </div>

      {error && (
        <div className="mt-3 bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

export default SeriesClassManagement;
